import React, { Fragment } from 'react';
import { Router } from '@reach/router';
import { useMappedState } from 'redux-react-hook';
import Loadable from 'react-loadable';
import Loading from 'components/Loading'

// Redux
import { getPlayingSong } from 'store/modules/player/selectors';
import { isLoading } from 'store/modules/app/selectors';

// Components
import LoadingBar from 'components/Loading/Bar';
import Player from 'containers/Player';

// Pages
import AuthScreen from 'containers/Layout/AuthScreen';
import Main from 'containers/Layout/Main';
import NotFound from 'containers/Layout/NotFound';

// Async pages
const AsyncIcons = Loadable({
  loader: () => import('pages/Public/Icons'),
  loading: Loading
});
const AsyncDiscover = Loadable({
  loader: () => import('pages/Public/Discover'),
  loading: Loading
});
const AsyncAlbum = Loadable({
  loader: () => import('pages/Public/Album'),
  loading: Loading
});
const AsyncSong = Loadable({
  loader: () => import('pages/Public/Song'),
  loading: Loading
});
const AsyncBrowse = Loadable({
  loader: () => import('pages/Public/Browse'),
  loading: Loading
});
const AsyncForum = Loadable({
  loader: () => import('pages/Public/Forum'),
  loading: Loading
});
const AsyncPlaylistDetail = Loadable({
  loader: () => import('pages/Members/Playlists/PlaylistDetail'),
  loading: Loading
});
const AsyncBlog = Loadable({
  loader: () => import('pages/Public/Blog'),
  loading: Loading
});
const AsyncPost = Loadable({
  loader: () => import('pages/Public/Blog/Post'),
  loading: Loading
});
const AsyncArtists = Loadable({
  loader: () => import('pages/Public/Artists'),
  loading: Loading
});
const AsyncProfile = Loadable({
  loader: () => import('pages/Members/Profile'),
  loading: Loading
});
const AsyncFavorites = Loadable({
  loader: () => import('pages/Members/Favorites'),
  loading: Loading
});
const AsyncHistory = Loadable({
  loader: () => import('pages/Members/History'),
  loading: Loading
});
const AsyncPlaylists = Loadable({
  loader: () => import('pages/Members/Playlists'),
  loading: Loading
});
const AsyncInbox = Loadable({
  loader: () => import('pages/Members/Mailbox/Inbox'),
  loading: Loading
});
const AsyncThread = Loadable({
  loader: () => import('pages/Members/Mailbox/Thread'),
  loading: Loading
});
const AsyncCompose = Loadable({
  loader: () => import('pages/Members/Mailbox/Compose'),
  loading: Loading
});
const AsyncPaywall = Loadable({
  loader: () => import('pages/Members/Paywall'),
  loading: Loading
});

// Authors
const AsyncAlbums = Loadable({
  loader: () => import('pages/Authors/Albums'),
  loading: Loading
});
const AsyncSingles = Loadable({
  loader: () => import('pages/Authors/Singles'),
  loading: Loading
});
const AsyncPodcasts = Loadable({
  loader: () => import('pages/Authors/Podcasts'),
  loading: Loading
});
const AsyncPosts = Loadable({
  loader: () => import('pages/Authors/Blog'),
  loading: Loading
});

// Admin pages
const AsyncDashboard = Loadable({
  loader: () => import('pages/Admin/Dashboard'),
  loading: Loading
});
const AsyncSubscriptions = Loadable({
  loader: () => import('pages/Admin/Subscriptions'),
  loading: Loading
});
const AsyncConfigurations = Loadable({
  loader: () => import('pages/Admin/Configurations'),
  loading: Loading
});
const AsyncUsers = Loadable({
  loader: () => import('pages/Admin/Users'),
  loading: Loading
});
const AsyncGenres = Loadable({
  loader: () => import('pages/Admin/Genres'),
  loading: Loading
});
const AsyncComments = Loadable({
  loader: () => import('pages/Admin/Comments'),
  loading: Loading
});
const AsyncAdManager = Loadable({
  loader: () => import('pages/Admin/AdManager'),
  loading: Loading
});

// Auth page
const AsyncSignIn = Loadable({
  loader: () => import('pages/Auth/SignIn'),
  loading: Loading
});
const AsyncSignUp = Loadable({
  loader: () => import('pages/Auth/SignUp'),
  loading: Loading
});
const AsyncRecover = Loadable({
  loader: () => import('pages/Auth/Recover'),
  loading: Loading
});
const AsyncUpdatePwd = Loadable({
  loader: () => import('pages/Auth/UpdatePwd'),
  loading: Loading
});
const AsyncConfirm = Loadable({
  loader: () => import('pages/Auth/Confirm'),
  loading: Loading
});

const mapState = state => ({
  loading: isLoading(state),
  song: getPlayingSong(state),
});

export default function App() {
  const { loading, song } = useMappedState(mapState);

  return (
    <Fragment>
      <Router>
        <Main path="/" isPublic>
          <AsyncDiscover path="/" />
          <AsyncDiscover path="/app/discover" />
          <AsyncAlbum path="/app/albums/:albumId" />
          <AsyncAlbum path="/app/albums/:albumId/:songId" />
          <AsyncSong path="/app/songs/:songId" />
          <AsyncBrowse path="/app/browse" />
          <AsyncForum path="/app/forum/*" />
          <AsyncBlog path="/app/blog" />
          <AsyncPost path="/app/blog/:postId" />
          <AsyncArtists path="/app/artists" />
          <AsyncIcons path="/app/icons" />
          <NotFound default />
        </Main>
        <Main path="/app/members" isPublic={false}>
          <AsyncFavorites path="/favorites" />
          <AsyncHistory path="/history" />
          <AsyncPlaylists path="/playlists" />
          <AsyncProfile path="/profiles/:userId/*" />
          <AsyncPlaylistDetail path="/playlists/:id" />
          <AsyncInbox path="/mailbox/:list" />
          <AsyncCompose path="/mailbox/compose/:userId" />
          <AsyncThread path="/mailbox/message/:threadId" />

          <NotFound default />
        </Main>
        <Main path="/app/authors" onlyAuthors>
          <AsyncAlbums path="/albums/*" />
          <AsyncSingles path="/singles/*" />
          <AsyncPodcasts path="/podcasts/*" />
          <AsyncPosts path="/blog/*" />
          <NotFound default />
        </Main>
        <Main path="/app/admin" onlyAdmins>
          <AsyncAdManager path="/ads/*" />
          <AsyncDashboard path="/dashboard" />
          <AsyncSubscriptions path="/billing/*" />
          <AsyncConfigurations path="/config/*" />
          <AsyncPosts path="/blog/*" admin />
          <AsyncUsers path="/users/*" />
          <AsyncGenres path="/genres/*" />
          <AsyncComments path="/comments/*" />
          <AsyncAlbums path="/albums/*" admin />
          <AsyncSingles path="/singles/*" admin />
          <AsyncPodcasts path="/podcasts/*" admin />
          <NotFound default />
        </Main>
        <AuthScreen path="/app/auth">
          <AsyncSignIn path="sign-in" />
          <AsyncSignUp path="sign-up" />
          <AsyncRecover path="forgot" />
          <AsyncUpdatePwd path="recover/:token" />
          <AsyncConfirm path="confirm/:token" />
        </AuthScreen>

        <AsyncPaywall path="/app/paywall/*" />
      </Router>
      {song && <Player />}
      {loading && <LoadingBar className="fixed inset-x-0 top-0 z-50" />}
    </Fragment>
  );
}
